import { useTranslation } from 'react-i18next'
import { PRODUCTS, type UsageSummary } from '../lib/types'
import { useProductTab } from '../hooks/useProductTab'
import { useUsageFilters } from '../hooks/useUsageFilters'
import { useUsagePageData } from '../hooks/useUsagePageData'
import { Tabs } from '../components/ui/Tabs'
import { PageScrollArea, PageView } from '../components/ui/PageScrollArea'
import { PageHeader } from '../components/ui/PageHeader'
import { Card, CardHeader, CardBody } from '../components/ui/Card'
import { MultiSelect } from '../components/ui/MultiSelect'
import { FilterLabel } from '../components/ui/FilterLabel'
import { MonthPicker } from '../components/ui/MonthPicker'
import { ProductViewGate } from '../components/ProductViewGate'
import { QueryStateCard } from '../components/ui/QueryStateCard'
import { UsageBarRow, UsageBreakdownList } from '../components/UsageBars'

function AccountSummaryCard({
  isAgents,
  summary,
}: Readonly<{ isAgents: boolean; summary: UsageSummary }>) {
  const { t } = useTranslation()
  return (
    <Card collapsible>
      <CardHeader
        title={t('usage.account.title')}
        subtitle={t(isAgents ? 'usage.account.subtitleAgt' : 'usage.account.subtitleMcp')}
      />
      <CardBody>
        {/* The account hero bar: scaled to its own total, so it is always full
            width — the Consumed figure as one bar. */}
        <UsageBarRow
          label={t('usage.allTenants')}
          total={summary.consumedVolume}
          max={summary.consumedVolume || 1}
        />
      </CardBody>
    </Card>
  )
}

function BreakdownCard({
  title,
  subtitle,
  summary,
  rowsKey,
  emptyText,
  fallbackLabel,
}: Readonly<{
  title: string
  subtitle: string
  summary: UsageSummary
  rowsKey: 'byTenant' | 'byUser'
  emptyText: string
  fallbackLabel: string
}>) {
  return (
    <Card collapsible>
      <CardHeader title={title} subtitle={subtitle} />
      <CardBody>
        <UsageBreakdownList rows={summary[rowsKey]} emptyText={emptyText} fallbackLabel={fallbackLabel} />
      </CardBody>
    </Card>
  )
}

export function Usage() {
  const { t } = useTranslation()
  const { product, setProduct } = useProductTab('usage')
  const filters = useUsageFilters()
  const { tenantFilter, userFilter, setMonth, onTenantChange, onUserChange } = filters
  const {
    status,
    summary,
    isPending,
    isError,
    tenantOptions,
    userOptions,
    selectedMonth,
  } = useUsagePageData(product, filters)
  const isAgents = product === 'Mambu Agents'

  return (
    <PageView>
      <PageHeader title={t('usage.title')} subtitle={t('usage.subtitle')} />

      <Tabs value={product} onChange={setProduct} options={PRODUCTS} />

      <PageScrollArea>
        <ProductViewGate status={status} page="usage" product={product}>
          {() => (
            <>
              {/* The prototype's `.filter-card`: no card chrome, page-coloured
                  and sticky at the top of the scroll area, so the cards scroll
                  under it rather than bleeding through. */}
              <div
                data-testid="usage-filters"
                className="sticky top-0 z-[6] bg-[var(--bg-app)] pt-0.5 pb-3 mb-1.5 flex gap-3 flex-wrap items-end"
              >
                <div className="flex-1 min-w-[140px] flex flex-col gap-1">
                  {/* The eraser is always present on every clearable field here
                      (tenant/user/period) — matches the prototype's
                      `addFilterErasers()`, which never hides it based on whether
                      the field is currently at its default. */}
                  <FilterLabel label={t('usage.filter.tenant')} onClear={() => onTenantChange(null)} />
                  <MultiSelect
                    options={tenantOptions}
                    value={tenantFilter}
                    onChange={onTenantChange}
                    ariaLabel={t('usage.filter.tenant')}
                  />
                </div>
                <div className="flex-1 min-w-[140px] flex flex-col gap-1">
                  <FilterLabel label={t('usage.filter.user')} onClear={() => onUserChange(null)} />
                  <MultiSelect
                    options={userOptions}
                    value={userFilter}
                    onChange={onUserChange}
                    ariaLabel={t('usage.filter.user')}
                  />
                </div>
                <div className="flex-1 min-w-[140px] flex flex-col gap-1">
                  <FilterLabel label={t('usage.filter.period')} onClear={() => setMonth(null)} />
                  <MonthPicker
                    months={summary?.availableMonths ?? []}
                    value={selectedMonth}
                    onChange={setMonth}
                    ariaLabel={t('usage.filter.period')}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                {isError && <QueryStateCard state="error" message={t('usage.loadError')} />}
                {!isError && (isPending || !summary) && <QueryStateCard state="pending" />}
                {!isError && summary && (
                  <>
                    <AccountSummaryCard isAgents={isAgents} summary={summary} />
                    <BreakdownCard
                      title={t('usage.byTenant.title')}
                      subtitle={t(isAgents ? 'usage.byTenant.subtitleAgt' : 'usage.byTenant.subtitleMcp')}
                      summary={summary}
                      rowsKey="byTenant"
                      emptyText={t('usage.byTenant.empty')}
                      fallbackLabel={t('usage.unattributedTenant')}
                    />
                    <BreakdownCard
                      title={t('usage.byUser.title')}
                      subtitle={t(isAgents ? 'usage.byUser.subtitleAgt' : 'usage.byUser.subtitleMcp')}
                      summary={summary}
                      rowsKey="byUser"
                      emptyText={t('usage.byUser.empty')}
                      fallbackLabel={t('usage.unknownUser')}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </ProductViewGate>
      </PageScrollArea>
    </PageView>
  )
}
